// Create this new component file: components/DeleteFacilityModal.tsx

import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { AlertTriangle, Loader2 } from "lucide-react";
import { FacilityDetailsResponse } from "../types";

interface DeleteFacilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  facility: FacilityDetailsResponse | null;
  isLoading: boolean;
}

export const DeleteFacility: React.FC<DeleteFacilityModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  facility,
  isLoading,
}) => {
  if (!facility) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold">
                Delete Facility
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-600">
                This action cannot be undone.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-gray-900 mb-2">
              Are you sure you want to delete this facility?
            </p>
            <div className="bg-white rounded border px-3 py-2">
              <p className="font-medium text-gray-900">
                {facility.facilityNo} - {facility.facilityName}
              </p>
              <p className="text-sm text-gray-600">{facility.description}</p>
            </div>
          </div>

          <div className="mt-4 space-y-2">
            <p className="text-sm font-medium text-gray-900">
              This will permanently remove:
            </p>
            <ul className="text-sm text-gray-600 space-y-1 ml-4">
              <li>• All facility data and configurations</li>
              <li>• Associated systems ({facility.systemsCount} systems)</li>
              <li>• Connected networks ({facility.networksCount} networks)</li>
              <li>• Linked assets ({facility.assetsCount} assets)</li>
              <li>• Financial records and depreciation data</li>
              <li>• All uploaded documents and files</li>
            </ul>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isLoading}
            className="min-w-[120px]"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete Facility"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
